import * as React from 'react';
import { FC, useEffect, useRef } from 'react';
import fetchAPI from 'app/helpers/fetch';
import useVersion from 'admin-mojo/helpers/useVersion';
import useInputErrorsDebouncer from 'app/useInputErrorsDebouncer';
import type { Locales } from 'app/helpers/createLocalesContext';
import { translation } from 'app/helpers/createLocalesContext';
import type { Errors, Values, ChangeValues } from './types';
import Spinner from './Spinner';
import ProfileIcon from './images/icon_profile.svg';

const TIMEOUT = 300;

const clearForbiddenSymbols = (str: string) => str.replace(/[^A-Za-z0-9]/g, '');

const Login: FC<{
  values: Values;
  changeValues: ChangeValues;
  errors: Errors;
  locales: Locales;
  isValidating: boolean;
}> = ({ values, changeValues, errors, locales, isValidating }) => {
  const autoLogin = useRef(true);
  const timerRef = useRef<number>();
  const suggestedLoginSource = values.email.split('@')[0];
  const [, rerender] = useVersion();
  const [canShowErrors, delayErrors] = useInputErrorsDebouncer();
  const showErrors = values.login && canShowErrors;

  const getSuggestion = (source: string) => {
    if (!source.length) return;

    window.clearTimeout(timerRef.current);

    const search = new URLSearchParams({ source }).toString();
    const tid = window.setTimeout(() => {
      fetchAPI(`/players/quick_sign_up_login_suggestion?${search}`, { method: 'GET', body: null })
        .then(({ login }) => {
          if (autoLogin.current && timerRef.current === tid) {
            changeValues({ login });
          }
        })
        .catch((err: string) => console.error(err))
        .finally(() => {
          if (timerRef.current === tid) {
            timerRef.current = undefined;
            rerender();
          }
        });
    }, TIMEOUT);
    timerRef.current = tid;
  };

  useEffect(() => {
    if (!autoLogin.current) return;
    if (values.email) {
      getSuggestion(clearForbiddenSymbols(suggestedLoginSource));
    } else {
      changeValues({ login: '' });
      timerRef.current = undefined;
    }
  }, [suggestedLoginSource]);

  const { login } = values;
  const loginErrors = errors.login || [];
  const notFormatErrors = loginErrors.filter((e) => e !== translation(locales, 'login.format_hint')).join(', ');

  return (
    <div className={`input player_login ${showErrors && loginErrors.length ? 'error' : ''}`}>
      <div className="input-with-icon xo-form_input-iconfull">
        <input
          autoComplete="off"
          placeholder={translation(locales, 'login.placeholder')}
          name="player[login]"
          id="player_login"
          value={login}
          className="sign_input xo-form_input"
          onChange={(e) => {
            autoLogin.current = false;
            changeValues({ login: clearForbiddenSymbols(e.target.value) });
            delayErrors();
          }}
        />
        <label htmlFor="player_login" className="xo-form_input-label">
          <span className="xo-form_label-placeholder">{translation(locales, 'login.placeholder')}</span>
          <span className="xo-form_input-image">
            <img src={ProfileIcon} alt="icon" />
          </span>
        </label>
        <span className="icon" />
        {(isValidating || !!timerRef.current) && <Spinner />}
      </div>
      <div className="hints">
        <span className="hint">{translation(locales, 'login.hint')}</span>
        <small
          className={
            showErrors && !autoLogin.current && loginErrors.includes(translation(locales, 'login.format_hint'))
              ? 'error'
              : 'hint'
          }
        >
          {translation(locales, 'login.format_hint')}
        </small>
        {showErrors && !autoLogin.current && notFormatErrors && <small className="error">{notFormatErrors}</small>}
      </div>
    </div>
  );
};

export default Login;
